import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { 
  Search, Trophy, Medal, Award, TrendingUp, TrendingDown, 
  Users, Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  BarChart3, Crown, AlertCircle, FileText, Filter, X
} from 'lucide-react';
import { Toast } from '../components/ui/Toast';
import useAppreciationsConfig from '../hooks/useAppreciationsConfig';
import { atteintLaMoyenne, baremeDeSection, ECHELLE_TRANCHES, partDuBareme, versEchelleTranches } from '../utils/baremeNotes';

// Le primaire se note sur 10, le collège et le lycée sur 20 (décision de
// Daniel, 7/10/2026). Le serveur donne le barème de chaque ligne
// (`bareme_max`) ; à défaut, on le tire de la section. Les mentions et les
// décisions de l'école sont réglées sur 20 : une moyenne du primaire y est
// ramenée (× 2) avant d'être comparée, et « admis » se lit à la moitié du
// barème (5/10, 10/20).
const baremeLigne = (eleve) => Number(eleve?.bareme_max) || baremeDeSection(eleve?.section_nom).max;

// ========================================
// CONSTANTES
// ========================================
const ITEMS_PER_PAGE = 20;

const StatistiquesGlobales = () => {
  // ========================================
  // HOOK APPRÉCIATIONS
  // ========================================
  const { 
    appreciationsResultat, 
    appreciationsNote, 
    loading: loadingConfig, 
    getAppreciation 
  } = useAppreciationsConfig();

  // ========================================
  // STATES
  // ========================================
  const [activeYear, setActiveYear] = useState(null);
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [moisScolaires, setMoisScolaires] = useState([]);

  // Sélections
  const [selectedSection, setSelectedSection] = useState('all');
  const [selectedTrimestre, setSelectedTrimestre] = useState('');
  const [selectedMois, setSelectedMois] = useState('');
  const [selectedClasse, setSelectedClasse] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filtres appréciation + décision
  const [filterAppreciation, setFilterAppreciation] = useState('all');
  const [filterDecision, setFilterDecision] = useState('all');
  const [isFilterAppOpen, setIsFilterAppOpen] = useState(false);
  const [isFilterDecOpen, setIsFilterDecOpen] = useState(false);

  // Dropdowns
  const [sectionSearchTerm, setSectionSearchTerm] = useState('');
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const [classeSearchTerm, setClasseSearchTerm] = useState('');
  const [isClasseDropdownOpen, setIsClasseDropdownOpen] = useState(false);
  const [isTrimestreDropdownOpen, setIsTrimestreDropdownOpen] = useState(false);
  const [isMoisDropdownOpen, setIsMoisDropdownOpen] = useState(false);

  // Données
  const [classements, setClassements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Vue + Pagination
  const [viewMode, setViewMode] = useState('global');
  const [expandedSections, setExpandedSections] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationParGroupe, setPaginationParGroupe] = useState({});

  // ========================================
  // FERMER LES DROPDOWNS AU CLIC EXTÉRIEUR
  // ========================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setIsSectionDropdownOpen(false);
        setIsClasseDropdownOpen(false);
        setIsTrimestreDropdownOpen(false);
        setIsMoisDropdownOpen(false);
        setIsFilterAppOpen(false);
        setIsFilterDecOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ========================================
  // CHARGEMENT INITIAL
  // ========================================
  useEffect(() => {
    const init = async () => {
      try {
        const [yearResult, classesResult, moisResult] = await Promise.all([
          api.anneesScolaires.getActive(),
          api.classes.getAll(),
          api.moisScolaires.getAll()
        ]);

        if (yearResult.success && yearResult.data) setActiveYear(yearResult.data);

        const allClasses = classesResult.success ? classesResult.data : classesResult;
        setClasses(allClasses);

        const sectionMap = {};
        allClasses.forEach(c => {
          if (c.section_nom && !sectionMap[c.section_nom]) {
            sectionMap[c.section_nom] = { id: c.section_id, nom: c.section_nom };
          }
        });
        setSections(Object.values(sectionMap));

        let mois = moisResult.success ? moisResult.data : moisResult;
        if (yearResult.data?.annee) mois = mois.filter(m => m.annee_scolaire === yearResult.data.annee);
        setMoisScolaires(mois);
      } catch (error) {
        console.error('Erreur initialisation:', error);
      }
    };
    init();
  }, []);

  // ========================================
  // HELPERS
  // ========================================
  const isPrimaire = useMemo(() => {
    if (selectedSection === 'all') return false;
    return selectedSection.toLowerCase() === 'primaire';
  }, [selectedSection]);

  const classesFiltrees = useMemo(() => {
    if (selectedSection === 'all') return classes;
    return classes.filter(c => c.section_nom === selectedSection);
  }, [classes, selectedSection]);

  const getSectionLabel = () => selectedSection === 'all' ? "Tout l'établissement" : selectedSection;
  const getClasseLabel = () => {
    if (selectedClasse === 'all') return 'Toutes les classes';
    return classes.find(c => c.id.toString() === selectedClasse)?.nom || '';
  };
  const getTrimestreLabel = () => ({ '1': '1er Trimestre', '2': '2ème Trimestre', '3': '3ème Trimestre' }[selectedTrimestre] || '');
  const getMoisLabel = () => {
    const m = moisScolaires.find(mo => mo.id.toString() === selectedMois);
    return m ? `${m.nom} (T${m.trimestre})` : '';
  };

  // Helpers appréciations : la moyenne est ramenée sur l'échelle des tranches
  // (sur 20) selon le barème de l'élève.
  const getAppreciationNote = (moyenne, baremeMax = ECHELLE_TRANCHES) => {
    return getAppreciation(versEchelleTranches(parseFloat(moyenne) || 0, baremeMax), 'note');
  };

  const getDecision = (moyenne, baremeMax = ECHELLE_TRANCHES) => {
    return getAppreciation(versEchelleTranches(parseFloat(moyenne) || 0, baremeMax), 'resultat');
  };

  // ========================================
  // CHARGER LES CLASSEMENTS
  // ========================================
  const chargerClassements = async () => {
    if (!activeYear) return;
    if (isPrimaire && !selectedMois) { setToast({ show: true, message: 'Veuillez sélectionner un mois', type: 'error' }); return; }
    if (!isPrimaire && !selectedTrimestre && selectedSection !== 'all') { setToast({ show: true, message: 'Veuillez sélectionner un trimestre', type: 'error' }); return; }
    if (selectedSection === 'all' && !selectedTrimestre) { setToast({ show: true, message: 'Veuillez sélectionner un trimestre', type: 'error' }); return; }

    try {
      setLoading(true);
      setCurrentPage(1);
      setPaginationParGroupe({});
      setFilterAppreciation('all');
      setFilterDecision('all');

      const params = new URLSearchParams({ annee_scolaire_id: activeYear.id });
      if (selectedSection !== 'all') params.append('section', selectedSection);
      if (selectedClasse !== 'all') params.append('classe_id', selectedClasse);
      if (isPrimaire && selectedMois) params.append('mois_id', selectedMois);
      else if (selectedTrimestre) params.append('trimestre', selectedTrimestre);

      const response = await api.scolarite.getStatistiquesGlobales(params.toString());
      if (response.success) { setClassements(response.data || []); setLoaded(true); }
      else setToast({ show: true, message: response.error || 'Erreur chargement', type: 'error' });
    } catch (error) {
      setToast({ show: true, message: `Erreur: ${error.message}`, type: 'error' });
    } finally { setLoading(false); }
  };

  // ========================================
  // DONNÉES FILTRÉES + TRIÉES
  // ========================================
  const donneesTriees = useMemo(() => {
    let data = [...classements];

    // Filtre recherche texte
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase().trim();
      data = data.filter(e => `${e.nom} ${e.prenom}`.toLowerCase().includes(s) || e.classe_nom?.toLowerCase().includes(s));
    }

    // Filtre appréciation
    if (filterAppreciation !== 'all') {
      data = data.filter(e => {
        const app = getAppreciationNote(e.moyenne, baremeLigne(e));
        return app && app.libelle === filterAppreciation;
      });
    }

    // Filtre décision
    if (filterDecision !== 'all') {
      data = data.filter(e => {
        const dec = getDecision(e.moyenne, baremeLigne(e));
        return dec && dec.libelle === filterDecision;
      });
    }

    // Tout l'établissement mêle le primaire (sur 10) et le secondaire (sur 20) :
    // on classe par part du barème atteinte, comme le serveur.
    data.sort((a, b) => partDuBareme(b.moyenne, baremeLigne(b)) - partDuBareme(a.moyenne, baremeLigne(a)));
    data.forEach((e, i) => { e.rang_global = i + 1; });
    return data;
  }, [classements, searchTerm, filterAppreciation, filterDecision, appreciationsNote, appreciationsResultat]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterAppreciation, filterDecision]);

  const totalPages = Math.ceil(donneesTriees.length / ITEMS_PER_PAGE);
  const donneesPaginees = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return donneesTriees.slice(start, start + ITEMS_PER_PAGE);
  }, [donneesTriees, currentPage]);

  // ========================================
  // STATS
  // ========================================
  const buildGroupStats = (data, groupKey) => {
    const stats = {};
    data.forEach(eleve => {
      const key = eleve[groupKey] || 'Autre';
      if (!stats[key]) stats[key] = { section: eleve.section_nom, bareme_max: baremeLigne(eleve), eleves: [], total: 0, somme: 0, max: 0, min: baremeLigne(eleve), admis: 0, echoues: 0 };
      const s = stats[key]; s.eleves.push(eleve); s.total++;
      const moy = parseFloat(eleve.moyenne) || 0; s.somme += moy;
      if (atteintLaMoyenne(moy, baremeLigne(eleve))) s.admis++; else s.echoues++;
      if (moy > s.max) s.max = moy; if (moy > 0 && moy < s.min) s.min = moy;
    });
    const rk = groupKey === 'section_nom' ? 'rang_section' : 'rang_classe';
    Object.values(stats).forEach(s => {
      s.moyenne = s.total > 0 ? (s.somme / s.total).toFixed(2) : '0.00';
      s.pourcentage = s.total > 0 ? ((s.admis / s.total) * 100).toFixed(1) : '0.0';
      s.eleves.sort((a, b) => parseFloat(b.moyenne) - parseFloat(a.moyenne));
      s.eleves.forEach((e, i) => { e[rk] = i + 1; });
    });
    return stats;
  };

  const statsParSection = useMemo(() => buildGroupStats(donneesTriees, 'section_nom'), [donneesTriees]);
  const statsParClasse = useMemo(() => buildGroupStats(donneesTriees, 'classe_nom'), [donneesTriees]);
  // Un seul barème (une section, une classe) : la moyenne s'écrit sur ce
  // barème. Primaire et secondaire mêlés (tout l'établissement) : chaque
  // moyenne est ramenée sur 20 avant d'être additionnée, et l'écran le dit.
  const baremesAffiches = useMemo(() => [...new Set(donneesTriees.map(baremeLigne))], [donneesTriees]);
  const baremeUnique = baremesAffiches.length === 1 ? baremesAffiches[0] : null;
  const afficherBaremeLigne = baremesAffiches.some(max => max !== ECHELLE_TRANCHES);
  const statsGlobales = useMemo(() => {
    if (!donneesTriees.length) return null;
    const lignes = donneesTriees.filter(e => (parseFloat(e.moyenne) || 0) > 0);
    if (!lignes.length) return null;
    const echelle = baremeUnique || ECHELLE_TRANCHES;
    const moyennes = lignes.map(e => (parseFloat(e.moyenne) * echelle) / baremeLigne(e));
    const admis = lignes.filter(e => atteintLaMoyenne(e.moyenne, baremeLigne(e))).length;
    return { total: donneesTriees.length, moyenne: (moyennes.reduce((a, b) => a + b, 0) / moyennes.length).toFixed(2), bareme_max: echelle, ramenee: !baremeUnique, admis, echoues: donneesTriees.length - admis, pourcentage: ((admis / donneesTriees.length) * 100).toFixed(1) };
  }, [donneesTriees, baremeUnique]);

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const getGroupPage = (gk) => paginationParGroupe[gk] || 1;
  const setGroupPage = (gk, p) => setPaginationParGroupe(prev => ({ ...prev, [gk]: p }));

  // Infos filtres actifs (pour le résumé)
  const hasActiveFilters = filterAppreciation !== 'all' || filterDecision !== 'all' || searchTerm.trim();
  const activeFilterCount = (filterAppreciation !== 'all' ? 1 : 0) + (filterDecision !== 'all' ? 1 : 0) + (searchTerm.trim() ? 1 : 0);

  const clearAllFilters = () => {
    setFilterAppreciation('all');
    setFilterDecision('all');
    setSearchTerm('');
    setCurrentPage(1);
  };


  const telechargerPDF = async () => {
  try {
    setToast({ show: true, message: 'Génération du PDF en cours...', type: 'loading' });

    const params = { annee_scolaire_id: activeYear.id };
    if (selectedSection !== 'all') params.section = selectedSection;
    if (selectedClasse !== 'all') params.classe_id = selectedClasse;
    if (isPrimaire && selectedMois) params.mois_id = selectedMois;
    else if (selectedTrimestre) params.trimestre = selectedTrimestre;
    if (filterAppreciation !== 'all') params.filtre_appreciation = filterAppreciation;
    if (filterDecision !== 'all') params.filtre_decision = filterDecision;

    const result = await api.scolarite.downloadStatistiquesPDF(params);
    if (result.success) {
      setToast({ show: true, message: 'PDF téléchargé avec succès', type: 'success' });
    } else {
      setToast({ show: true, message: result.error || 'Erreur génération PDF', type: 'error' });
    }
  } catch (err) {
    setToast({ show: true, message: err.message, type: 'error' });
  }
};




  // ========================================
  // SOUS-COMPOSANTS
  // ========================================
  const RangBadge = ({ rang }) => {
    const base = 'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold';
    if (rang === 1) return <div className={`${base} bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-md`}>1</div>;
    if (rang === 2) return <div className={`${base} bg-gradient-to-br from-gray-300 to-gray-400 text-white shadow-md`}>2</div>;
    if (rang === 3) return <div className={`${base} bg-gradient-to-br from-amber-600 to-amber-700 text-white shadow-md`}>3</div>;
    return <div className={`${base} bg-gray-100 text-gray-700`}>{rang}</div>;
  };

  const AppreciationBadge = ({ moyenne, baremeMax }) => {
    const app = getAppreciationNote(moyenne, baremeMax);
    if (!app || app.libelle === '-') return <span className="text-xs text-gray-400">-</span>;
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${app.couleur}`}>{app.libelle}</span>;
  };

  const DecisionBadge = ({ moyenne, baremeMax }) => {
    const dec = getDecision(moyenne, baremeMax);
    if (!dec || dec.libelle === '-') return <span className="text-xs text-gray-400">-</span>;
    return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${dec.couleur}`}>{dec.libelle}</span>;
  };

  const PaginationBar = ({ current, total, onChange }) => {
    if (total <= 1) return null;
    const maxV = 5;
    let start = Math.max(1, current - Math.floor(maxV / 2));
    let end = Math.min(total, start + maxV - 1);
    if (end - start + 1 < maxV) start = Math.max(1, end - maxV + 1);
    const pages = []; for (let i = start; i <= end; i++) pages.push(i);
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <span className="text-xs text-gray-500">Page {current} sur {total}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => onChange(current - 1)} disabled={current === 1}
            className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={16} /></button>
          {pages.map(p => (
            <button key={p} onClick={() => onChange(p)}
              className={`w-8 h-8 rounded text-xs font-medium transition-colors ${p === current ? 'bg-[#246990] text-white' : 'hover:bg-gray-200 text-gray-700'}`}>{p}</button>
          ))}
          <button onClick={() => onChange(current + 1)} disabled={current === total}
            className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronRight size={16} /></button>
        </div>
      </div>
    );
  };

  const ElevesTable = ({ eleves, rangKey = 'rang_global', showClasse = true, showSection = false, groupKey = null }) => {
    const isGlobal = !groupKey;
    const cPage = isGlobal ? currentPage : getGroupPage(groupKey);
    const tPages = Math.ceil(eleves.length / ITEMS_PER_PAGE);
    const items = eleves.slice((cPage - 1) * ITEMS_PER_PAGE, cPage * ITEMS_PER_PAGE);
    const onPageChange = isGlobal ? setCurrentPage : (p) => setGroupPage(groupKey, p);

    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#246990] to-[#3084b5] text-white">
              <tr>
                <th className="py-2.5 px-3 text-center font-medium text-xs w-10">N°</th>
                <th className="py-2.5 px-3 text-center font-medium text-xs w-14">Rang</th>
                <th className="py-2.5 px-3 text-left font-medium text-xs">Noms et Prénoms</th>
                {showClasse && <th className="py-2.5 px-3 text-left font-medium text-xs">Classe</th>}
                {showSection && <th className="py-2.5 px-3 text-left font-medium text-xs">Section</th>}
                <th className="py-2.5 px-3 text-center font-medium text-xs w-20">Moyenne</th>
                <th className="py-2.5 px-3 text-center font-medium text-xs w-28">Appréciation</th>
                <th className="py-2.5 px-3 text-center font-medium text-xs w-28">Décision</th>
              </tr>
            </thead>
            <tbody>
              {items.map((eleve, idx) => {
                const moy = parseFloat(eleve.moyenne) || 0;
                const baremeMax = baremeLigne(eleve);
                // Part du barème : 14/20 et 7/10 ont la même couleur.
                const part = partDuBareme(moy, baremeMax);
                const rang = eleve[rangKey] || idx + 1;
                const num = (cPage - 1) * ITEMS_PER_PAGE + idx + 1;
                return (
                  <tr key={`${eleve.eleve_id}-${idx}`}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${rang <= 3 ? 'bg-yellow-50/40' : ''} ${part < 0.5 ? 'bg-red-50/20' : ''}`}>
                    <td className="py-2 px-3 text-center text-xs text-gray-400">{num}</td>
                    <td className="py-2 px-3 text-center"><RangBadge rang={rang} /></td>
                    <td className="py-2 px-3"><span className={`font-medium text-sm ${rang <= 3 ? 'text-[#246990]' : 'text-gray-800'}`}>{eleve.nom} {eleve.prenom}</span></td>
                    {showClasse && <td className="py-2 px-3 text-xs text-gray-600">{eleve.classe_nom}</td>}
                    {showSection && <td className="py-2 px-3"><span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{eleve.section_nom}</span></td>}
                    <td className="py-2 px-3 text-center">
                      <span className={`font-bold text-sm ${part >= 0.7 ? 'text-emerald-600' : part >= 0.5 ? 'text-[#319190]' : part >= 0.4 ? 'text-orange-500' : 'text-red-500'}`}>
                        {moy.toFixed(2)}
                      </span>
                      {afficherBaremeLigne && <span className="text-xs text-gray-400">/{baremeMax}</span>}
                    </td>
                    <td className="py-2 px-3 text-center"><AppreciationBadge moyenne={moy} baremeMax={baremeMax} /></td>
                    <td className="py-2 px-3 text-center"><DecisionBadge moyenne={moy} baremeMax={baremeMax} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {items.length === 0 && (
            <div className="p-8 text-center text-gray-400 text-sm"><AlertCircle size={32} className="mx-auto mb-2" />Aucun résultat</div>
          )}
        </div>
        <PaginationBar current={cPage} total={tPages} onChange={onPageChange} />
      </>
    );
  };

  // ========================================
  // RENDER
  // ========================================
  if (!activeYear) {
    return <div className="flex justify-center items-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#246990]"></div></div>;
  }

  return (
    <div className="space-y-6">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />}

      {/* ============ HEADER ============ */}
      <div className="bg-gradient-to-r from-[#246990] to-[#3084b5] rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold mb-1 flex items-center gap-2"><BarChart3 size={22} /> Statistiques & Classements</h1>
            <p className="text-blue-100 text-sm">Classements par classe, section et établissement</p>
          </div>
          {loaded && donneesTriees.length > 0 && (


            <button onClick={() => telechargerPDF()} disabled={!loaded || donneesTriees.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-all text-sm disabled:opacity-30 disabled:cursor-not-allowed">
                <Download size={16} />
                <span className="hidden md:inline">Télécharger PDF</span>
                </button>


          )}
        </div>
      </div>

      {/* ============ FILTRES ============ */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

          {/* SECTION */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Section / Cycle</label>
            <div className="relative dropdown-container">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 pointer-events-none z-10" />
              <input type="text" placeholder="Sélectionner une section..."
                value={isSectionDropdownOpen ? sectionSearchTerm : getSectionLabel()}
                onChange={(e) => { setSectionSearchTerm(e.target.value); setIsSectionDropdownOpen(true); }}
                onFocus={() => { setIsSectionDropdownOpen(true); setSectionSearchTerm(''); }}
                className="w-full pl-10 pr-10 py-3 text-base font-medium border-2 border-gray-200 rounded-lg bg-white hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10 outline-none transition-all" />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {isSectionDropdownOpen && (
                <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {/* Tout l'établissement */}
                  <div onClick={() => { setSelectedSection('all'); setSelectedClasse('all'); setSelectedTrimestre(''); setSelectedMois(''); setClassements([]); setLoaded(false); setSectionSearchTerm(''); setIsSectionDropdownOpen(false); }}
                    className={`px-4 py-3 hover:bg-blue-50 cursor-pointer border-b transition-colors ${selectedSection === 'all' ? 'bg-blue-100 font-semibold' : ''}`}>
                    <div className="font-medium text-gray-900 text-base">Tout l'établissement</div>
                    <p className="text-xs text-gray-500">Toutes les sections confondues</p>
                  </div>
                  {sections
                    .filter(s => !sectionSearchTerm || s.nom.toLowerCase().includes(sectionSearchTerm.toLowerCase()))
                    .map(s => (
                    <div key={s.id}
                      onClick={() => { setSelectedSection(s.nom); setSelectedClasse('all'); setSelectedTrimestre(''); setSelectedMois(''); setClassements([]); setLoaded(false); setSectionSearchTerm(''); setIsSectionDropdownOpen(false); }}
                      className={`px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors ${selectedSection === s.nom ? 'bg-blue-100 font-semibold' : ''}`}>
                      <div className="font-medium text-gray-900 text-base">{s.nom}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CLASSE */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Classe (optionnel)</label>
            <div className="relative dropdown-container">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 pointer-events-none z-10" />
              <input type="text" placeholder="Toutes les classes..."
                value={isClasseDropdownOpen ? classeSearchTerm : getClasseLabel()}
                onChange={(e) => { setClasseSearchTerm(e.target.value); setIsClasseDropdownOpen(true); }}
                onFocus={() => { setIsClasseDropdownOpen(true); setClasseSearchTerm(''); }}
                className="w-full pl-10 pr-10 py-3 text-base font-medium border-2 border-gray-200 rounded-lg bg-white hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10 outline-none transition-all" />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {isClasseDropdownOpen && (
                <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  <div onClick={() => { setSelectedClasse('all'); setClassements([]); setLoaded(false); setClasseSearchTerm(''); setIsClasseDropdownOpen(false); }}
                    className={`px-4 py-3 hover:bg-blue-50 cursor-pointer border-b transition-colors ${selectedClasse === 'all' ? 'bg-blue-100 font-semibold' : ''}`}>
                    <div className="font-medium text-gray-900 text-base">Toutes les classes</div>
                  </div>
                  {(() => {
                    const filtered = classesFiltrees.filter(c => c.nom.toLowerCase().includes(classeSearchTerm.toLowerCase())).sort((a, b) => (a.ordre || 0) - (b.ordre || 0) || a.nom.localeCompare(b.nom));
                    const groups = {};
                    filtered.forEach(c => { const sec = c.section_nom || 'Autre'; if (!groups[sec]) groups[sec] = []; groups[sec].push(c); });
                    if (!Object.keys(groups).length) return <div className="p-4 text-center text-gray-500 text-sm">Aucune classe trouvée</div>;
                    return Object.entries(groups).map(([secNom, cls]) => (
                      <div key={secNom}>
                        <div className="px-4 py-2 bg-gray-100 border-b font-bold text-gray-700 text-sm">{secNom}</div>
                        {cls.map(classe => (
                          <div key={classe.id}
                            onClick={() => { setSelectedClasse(classe.id.toString()); setClassements([]); setLoaded(false); setClasseSearchTerm(''); setIsClasseDropdownOpen(false); }}
                            className={`px-4 py-3 pl-8 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors ${selectedClasse === classe.id.toString() ? 'bg-blue-100' : ''}`}>
                            <div className="font-medium text-gray-900 text-base">{classe.nom}</div>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* PÉRIODE */}
          {isPrimaire ? (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Mois <span className="text-red-500">*</span></label>
              <div className="relative dropdown-container">
                <input type="text" placeholder="Sélectionner un mois..."
                  value={isMoisDropdownOpen ? '' : getMoisLabel()}
                  onFocus={() => setIsMoisDropdownOpen(true)} readOnly
                  className="w-full px-4 py-3 pr-10 text-base font-medium border-2 border-gray-200 rounded-lg bg-white hover:border-[#319190] focus:border-[#319190] focus:ring-4 focus:ring-[#319190]/10 outline-none transition-all cursor-pointer" />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                {isMoisDropdownOpen && (
                  <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {moisScolaires.sort((a, b) => a.ordre - b.ordre).map(m => (
                      <div key={m.id} onClick={() => { setSelectedMois(m.id.toString()); setClassements([]); setLoaded(false); setIsMoisDropdownOpen(false); }}
                        className={`px-4 py-3 hover:bg-teal-50 cursor-pointer border-b last:border-b-0 transition-colors ${selectedMois === m.id.toString() ? 'bg-teal-100' : ''}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 text-base">{m.nom}</span>
                          <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">T{m.trimestre}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Trimestre <span className="text-red-500">*</span></label>
              <div className="relative dropdown-container">
                <input type="text" placeholder="Sélectionner un trimestre..."
                  value={isTrimestreDropdownOpen ? '' : getTrimestreLabel()} onFocus={() => setIsTrimestreDropdownOpen(true)} readOnly
                  className="w-full px-4 py-3 pr-10 text-base font-medium border-2 border-gray-200 rounded-lg bg-white hover:border-[#319190] focus:border-[#319190] focus:ring-4 focus:ring-[#319190]/10 outline-none transition-all cursor-pointer" />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                {isTrimestreDropdownOpen && (
                  <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
                    {[{ value: '1', label: '1er Trimestre' }, { value: '2', label: '2ème Trimestre' }, { value: '3', label: '3ème Trimestre' }].map(t => (
                      <div key={t.value} onClick={() => { setSelectedTrimestre(t.value); setClassements([]); setLoaded(false); setIsTrimestreDropdownOpen(false); }}
                        className={`px-4 py-3 hover:bg-teal-50 cursor-pointer border-b last:border-b-0 transition-colors ${selectedTrimestre === t.value ? 'bg-teal-100' : ''}`}>
                        <div className="font-medium text-gray-900 text-base">{t.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* BOUTON */}
          <div className="flex items-end">
            <button onClick={chargerClassements} disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#246990] to-[#3084b5] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base">
              {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <BarChart3 size={18} />}
              {loading ? 'Chargement...' : 'Afficher les résultats'}
            </button>
          </div>
        </div>
      </div>

      {/* ============ RÉSULTATS ============ */}
      {loaded && (
        <>
          {/* CARDS */}
          {statsGlobales && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-[#246990]">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-1"><Users size={14} className="text-[#246990]" />Effectif</div>
                <div className="text-2xl font-bold text-[#246990]">{statsGlobales.total}</div>
                <div className="text-xs text-gray-500">élèves classés</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-[#319190]">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-1"><TrendingUp size={14} className="text-[#319190]" />Moyenne</div>
                <div className="text-2xl font-bold text-[#319190]">{statsGlobales.moyenne}<span className="text-sm text-gray-400">/{statsGlobales.bareme_max}</span></div>
                {statsGlobales.ramenee && <div className="text-xs text-gray-500">primaire ramené sur {statsGlobales.bareme_max}</div>}
              </div>
              <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-green-500">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-1"><Trophy size={14} className="text-green-500" />Admis</div>
                <div className="text-2xl font-bold text-green-600">{statsGlobales.admis} <span className="text-sm text-gray-400">{statsGlobales.pourcentage}%</span></div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-red-400">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-1"><TrendingDown size={14} className="text-red-400" />Échoués</div>
                <div className="text-2xl font-bold text-red-500">{statsGlobales.echoues} <span className="text-sm text-gray-400">{(100 - parseFloat(statsGlobales.pourcentage)).toFixed(1)}%</span></div>
              </div>
            </div>
          )}

          {/* PODIUM */}
          {donneesTriees.length >= 3 && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Crown size={18} className="text-yellow-500" />
                Podium, Top 3 {selectedSection === 'all' ? "de l'établissement" : ` ${selectedSection}`}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center pt-6">
                  <Medal size={26} className="text-gray-400 mb-1" />
                  <p className="font-bold text-gray-800 text-sm text-center">{donneesTriees[1]?.nom} {donneesTriees[1]?.prenom}</p>
                  <p className="text-xs text-gray-500">{donneesTriees[1]?.classe_nom}</p>
                  <p className="text-lg font-bold text-gray-500">{parseFloat(donneesTriees[1]?.moyenne).toFixed(2)}{afficherBaremeLigne && <span className="text-xs font-medium text-gray-400">/{baremeLigne(donneesTriees[1])}</span>}</p>
                  <div className="w-full bg-gray-200 rounded-t-lg mt-2 h-16 flex items-center justify-center"><span className="text-xl font-bold text-gray-500">2</span></div>
                </div>
                <div className="flex flex-col items-center">
                  <Crown size={30} className="text-yellow-500 mb-1" />
                  <p className="font-bold text-[#246990] text-center">{donneesTriees[0]?.nom} {donneesTriees[0]?.prenom}</p>
                  <p className="text-xs text-gray-500">{donneesTriees[0]?.classe_nom}</p>
                  <p className="text-2xl font-bold text-yellow-600">{parseFloat(donneesTriees[0]?.moyenne).toFixed(2)}{afficherBaremeLigne && <span className="text-xs font-medium text-gray-400">/{baremeLigne(donneesTriees[0])}</span>}</p>
                  <div className="w-full bg-gradient-to-t from-yellow-400 to-yellow-300 rounded-t-lg mt-2 h-24 flex items-center justify-center"><span className="text-2xl font-bold text-white">1</span></div>
                </div>
                <div className="flex flex-col items-center pt-10">
                  <Award size={22} className="text-amber-600 mb-1" />
                  <p className="font-bold text-gray-800 text-sm text-center">{donneesTriees[2]?.nom} {donneesTriees[2]?.prenom}</p>
                  <p className="text-xs text-gray-500">{donneesTriees[2]?.classe_nom}</p>
                  <p className="text-lg font-bold text-amber-700">{parseFloat(donneesTriees[2]?.moyenne).toFixed(2)}{afficherBaremeLigne && <span className="text-xs font-medium text-gray-400">/{baremeLigne(donneesTriees[2])}</span>}</p>
                  <div className="w-full bg-amber-200 rounded-t-lg mt-2 h-12 flex items-center justify-center"><span className="text-xl font-bold text-amber-700">3</span></div>
                </div>
              </div>
            </div>
          )}

          {/* TABLEAU */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Toolbar */}
            <div className="p-4 border-b border-gray-200 space-y-3">
              {/* Ligne 1 : Vue + Recherche + PDF */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <h3 className="font-bold text-gray-800 text-sm">Classement détaillé</h3>
                  <div className="flex bg-gray-100 rounded-lg p-0.5">
                    {[{ v: 'global', l: 'Global' }, { v: 'section', l: 'Par section' }, { v: 'classe', l: 'Par classe' }].map(m => (
                      <button key={m.v} onClick={() => { setViewMode(m.v); setCurrentPage(1); setPaginationParGroupe({}); }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === m.v ? 'bg-[#246990] text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>{m.l}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="text" placeholder="Rechercher un élève..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none w-52" />
                  </div> 

                </div>
              </div>

              {/* Ligne 2 : Filtres Appréciation + Décision */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Filter size={13} />
                  <span className="font-medium">Filtrer par :</span>
                </div>

                {/* Filtre Appréciation */}
                <div className="relative dropdown-container">
                  <button onClick={() => { setIsFilterAppOpen(!isFilterAppOpen); setIsFilterDecOpen(false); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      filterAppreciation !== 'all' ? 'bg-[#319190]/10 border-[#319190] text-[#319190] font-semibold' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}>
                    Appréciation{filterAppreciation !== 'all' ? `: ${filterAppreciation}` : ''}
                    <ChevronDown size={12} />
                  </button>
                  {isFilterAppOpen && (
                    <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg min-w-48 overflow-hidden">
                      <div onClick={() => { setFilterAppreciation('all'); setIsFilterAppOpen(false); }}
                        className={`px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer border-b ${filterAppreciation === 'all' ? 'bg-gray-100 font-semibold' : ''}`}>
                        Toutes les appréciations
                      </div>
                      {appreciationsNote.map(app => (
                        <div key={app.id} onClick={() => { setFilterAppreciation(app.libelle); setIsFilterAppOpen(false); }}
                          className={`px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between ${filterAppreciation === app.libelle ? 'bg-gray-100 font-semibold' : ''}`}>
                          <span>{app.libelle}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${app.couleur}`}>{parseFloat(app.note_min).toFixed(0)}-{parseFloat(app.note_max).toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Filtre Décision */}
                <div className="relative dropdown-container">
                  <button onClick={() => { setIsFilterDecOpen(!isFilterDecOpen); setIsFilterAppOpen(false); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      filterDecision !== 'all' ? 'bg-[#246990]/10 border-[#246990] text-[#246990] font-semibold' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}>
                    Décision{filterDecision !== 'all' ? `: ${filterDecision}` : ''}
                    <ChevronDown size={12} />
                  </button>
                  {isFilterDecOpen && (
                    <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg min-w-48 overflow-hidden">
                      <div onClick={() => { setFilterDecision('all'); setIsFilterDecOpen(false); }}
                        className={`px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer border-b ${filterDecision === 'all' ? 'bg-gray-100 font-semibold' : ''}`}>
                        Toutes les décisions
                      </div>
                      {appreciationsResultat.map(dec => (
                        <div key={dec.id} onClick={() => { setFilterDecision(dec.libelle); setIsFilterDecOpen(false); }}
                          className={`px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between ${filterDecision === dec.libelle ? 'bg-gray-100 font-semibold' : ''}`}>
                          <span>{dec.libelle}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${dec.couleur}`}>{parseFloat(dec.note_min).toFixed(0)}-{parseFloat(dec.note_max).toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Indicateur filtres actifs + clear */}
                {hasActiveFilters && (
                  <button onClick={clearAllFilters}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors font-medium">
                    <X size={12} /> Effacer {activeFilterCount} filtre{activeFilterCount > 1 ? 's' : ''}
                  </button>
                )}

                {/* Compteur résultats */}
                {loaded && (
                  <span className="text-xs text-gray-400 ml-auto">
                    {donneesTriees.length} élève{donneesTriees.length > 1 ? 's' : ''} 
                    {hasActiveFilters ? ` sur ${classements.length}` : ''}
                  </span>
                )}
              </div>
            </div>

            {viewMode === 'global' && <ElevesTable eleves={donneesTriees} rangKey="rang_global" showClasse showSection />}

            {viewMode === 'section' && (
              <div className="divide-y divide-gray-200">
                {Object.entries(statsParSection).map(([nom, stats]) => (
                  <div key={nom}>
                    <div onClick={() => toggleSection(`s_${nom}`)} className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-[#246990]">{expandedSections[`s_${nom}`] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
                        <div>
                          <h4 className="font-bold text-gray-800 text-sm">{nom}</h4>
                          <p className="text-xs text-gray-500">{stats.total} élèves • Moy: {stats.moyenne}/{stats.bareme_max} • {stats.pourcentage}% admis</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{stats.admis} admis</span>
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">{stats.echoues} éch.</span>
                        <button onClick={(e) => { e.stopPropagation(); setToast({ show: true, message: `PDF ${nom}...`, type: 'loading' }); }}
                          className="p-1.5 text-[#246990] hover:bg-blue-50 rounded-lg transition-colors" title={`Télécharger PDF ${nom}`}><Download size={16} /></button>
                      </div>
                    </div>
                    {expandedSections[`s_${nom}`] && (
                      <div className="px-4 pb-4"><ElevesTable eleves={stats.eleves} rangKey="rang_section" showClasse groupKey={`section_${nom}`} /></div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {viewMode === 'classe' && (
              <div className="divide-y divide-gray-200">
                {Object.entries(statsParClasse).map(([nom, stats]) => (
                  <div key={nom}>
                    <div onClick={() => toggleSection(`c_${nom}`)} className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-[#246990]">{expandedSections[`c_${nom}`] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
                        <div>
                          <h4 className="font-bold text-gray-800 text-sm">{nom}</h4>
                          <p className="text-xs text-gray-500">{stats.total} élèves • Moy: {stats.moyenne}/{stats.bareme_max} • {stats.pourcentage}% de réussite</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{stats.admis} admis</span>
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">{stats.echoues} éch.</span>
                        <button onClick={(e) => { e.stopPropagation(); setToast({ show: true, message: `PDF ${nom}...`, type: 'loading' }); }}
                          className="p-1.5 text-[#246990] hover:bg-blue-50 rounded-lg transition-colors" title={`Télécharger PDF ${nom}`}><Download size={16} /></button>
                      </div>
                    </div>
                    {expandedSections[`c_${nom}`] && (
                      <div className="px-4 pb-4"><ElevesTable eleves={stats.eleves} rangKey="rang_classe" showClasse={false} groupKey={`classe_${nom}`} /></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !loading && (
        <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-6">
          <p className="text-blue-800 font-medium">Sélectionnez vos filtres et cliquez sur "Afficher les résultats" pour voir les classements.</p>
        </div>
      )}
    </div>
  );
};

export default StatistiquesGlobales;